import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import {
  EmptyOrganisationViewModel,
  OrganisationViewModel,
} from '../models/organisation-view-model'
import {
  CLEAR_ORGANISATION_STATISTICS,
  CREATE_ORGANISATION_POST,
  DELETE_ORGANISATION_POST,
  FETCH_ORGANISATION,
  FETCH_ORGANISATION_ACTIVITIES,
  FETCH_ORGANISATION_INTERACTIONS,
  FETCH_ORGANISATION_POSTS,
  FETCH_ORGANISATION_STATISTICS,
  FETCH_ORGANISATIONS,
  SEARCH_ORGANISATIONS,
  UPDATE_ORGANISATION,
  UPDATE_ORGANISATION_POST,
} from '@/business/organisation/store/types'
import { Api } from '@/api/api'
import { Loading } from '@/shared/utils/vuex-loading'
import {
  GetOrganisationsRequest,
  OrganisationActivitiesPageResponse,
  OrganisationInteractionsPageResponse,
  OrganisationPageResponse,
  OrganisationResponse,
  PostPageResponse,
  PostResponse,
  StatisticsResponse,
  UpdateOrganisationRequest,
} from '@/api/generated'
import { OrganisationState } from '@/business/organisation/store/organisation-state'
import { FetchPagePayload } from '@/business/organisation/interfaces/fetch-page-payload'
import {
  EmptyOrganisationsViewModel,
  OrganisationsViewModel,
} from '@/business/organisation/models/organisations-view-model'
import {
  EmptyOrganisationStatisticsViewModel,
  OrganisationStatisticsViewModel,
} from '@/business/organisation/models/organisation-statistics-view-model'
import { updateOrDiscard, updateOrPrepend, withoutEntry } from '@/shared/utils/list'
import { CreateOrganisationPostPayload } from '@/business/organisation/store/models/create-organisation-post-payload'
import { OrganisationActivitiesPayload } from '@/business/organisation/store/models/organisation-activities-payload'
import { OrganisationInteractionsPayload } from '@/business/organisation/store/models/organisation-interactions-payload'
import { Post } from '@/business/post/models/post'
import { UpdateOrganisationPostPayload } from '@/business/organisation/store/models/update-organisation-post-payload'
import { DeleteOrganisationPostPayload } from '@/business/organisation/store/models/delete-organisation-post-payload'
import { FetchOrganisationPostsPayload } from '@/business/organisation/store/models/fetch-organisation-posts-payload'
import { updatePostsPage } from '@/shared/utils/post-helpers/update-posts-page'
import { emptyPageResponse } from '@/shared/interfaces/page-response'
import { mergePages } from '@/shared/utils/post-helpers/merge-pages'
import { environmentConfiguration } from '@/config/environments'

const STORE_NAME = 'organisation'

@Module({
  name: STORE_NAME,
})
export class OrganisationsModule extends VuexModule implements OrganisationState {
  public static readonly STORE_NAME: string = STORE_NAME

  public organisationStatisticsViewModel: OrganisationStatisticsViewModel = EmptyOrganisationStatisticsViewModel
  public organisationViewModel: OrganisationViewModel = EmptyOrganisationViewModel
  public organisationsViewModel: OrganisationsViewModel = EmptyOrganisationsViewModel

  @Mutation
  public clearOrganisation() {
    this.organisationViewModel = EmptyOrganisationViewModel
  }

  @Mutation
  public [CLEAR_ORGANISATION_STATISTICS]() {
    this.organisationStatisticsViewModel = EmptyOrganisationStatisticsViewModel
  }

  @Mutation
  public [CREATE_ORGANISATION_POST](post: PostResponse) {
    this.organisationViewModel.posts = updatePostsPage(
      this.organisationViewModel.posts,
      post,
      updateOrPrepend,
      1
    )
  }

  @Mutation
  public [DELETE_ORGANISATION_POST](post: Post) {
    this.organisationViewModel.posts = updatePostsPage(
      this.organisationViewModel.posts,
      post,
      withoutEntry,
      -1
    )
  }

  @Mutation
  public [FETCH_ORGANISATION](vm: OrganisationViewModel) {
    this.organisationViewModel = vm
  }

  @Mutation
  public [FETCH_ORGANISATION_ACTIVITIES](activities: OrganisationActivitiesPageResponse) {
    this.organisationViewModel.activities = activities
  }

  @Mutation
  public [FETCH_ORGANISATION_INTERACTIONS](interactions: OrganisationInteractionsPageResponse) {
    this.organisationViewModel.interactions = interactions
  }

  @Mutation
  public [FETCH_ORGANISATION_POSTS](posts: PostPageResponse) {
    this.organisationViewModel.posts = mergePages(
      this.organisationViewModel.posts,
      posts,
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    )
  }

  @Mutation
  public [FETCH_ORGANISATIONS](all: any) {
    this.organisationsViewModel = all
  }

  @Mutation
  public [FETCH_ORGANISATION_STATISTICS](statistics: StatisticsResponse) {
    this.organisationStatisticsViewModel.numberOfVisits = statistics.numberOfVisits
    this.organisationStatisticsViewModel.visits = statistics.visits
  }

  @Mutation
  public [SEARCH_ORGANISATIONS](organisations: OrganisationPageResponse) {
    this.organisationsViewModel.organisations = organisations
  }

  @Mutation
  public [UPDATE_ORGANISATION](organisation: OrganisationResponse) {
    this.organisationViewModel.organisation = organisation
  }

  @Mutation
  public [UPDATE_ORGANISATION_POST](post: Post) {
    this.organisationViewModel.posts = updatePostsPage(
      this.organisationViewModel.posts,
      post,
      updateOrDiscard
    )
  }

  @Loading(CREATE_ORGANISATION_POST)
  @Action({
    commit: CREATE_ORGANISATION_POST,
    rawError: true,
  })
  public async createOrganisationPost(
    payload: CreateOrganisationPostPayload
  ): Promise<PostResponse> {
    return Api.Organisations.createOrganisationPost({
      organisationId: payload.organisationId,
      postRequest: payload.post,
    })
  }

  @Loading(DELETE_ORGANISATION_POST)
  @Action({
    commit: DELETE_ORGANISATION_POST,
    rawError: true,
  })
  deleteOrganisationPost({ post }: DeleteOrganisationPostPayload): Promise<Post> {
    return Api.Posts.deletePost({
      postId: post.id,
    }).then(() => post)
  }

  @Loading(FETCH_ORGANISATION)
  @Action({
    commit: FETCH_ORGANISATION,
    rawError: true,
  })
  public async fetchOrganisation(id: number): Promise<OrganisationViewModel> {
    const categories$ = Api.Lookup.getCategories().catch(() => [])
    const organisationsTypes$ = Api.Lookup.getOrganisationTypes().catch(() => [])

    const sectors$ = Api.Lookup.getOrganisationSectors().catch(() => [])

    const offers$ = Api.Organisations.getOffersOfOrganisation({
      organisationId: id,
      page: 1,
      pageSize: 1000,
    })

    const organisation$ = Api.Organisations.getOrganisation({
      organisationId: id,
    })

    const projects$ = Api.Organisations.getProjectsOfOrganisation({
      includePartnerProjects: !environmentConfiguration().branding,
      organisationId: id,
      page: 1,
      pageSize: 1000,
    })

    return Promise.all([
      offers$,
      organisation$,
      categories$,
      organisationsTypes$,
      sectors$,
      projects$,
    ]).then(([offers, organisation, categories, organisationTypes, sectors, projects]) => {
      return {
        activities: this.organisationViewModel.activities,
        interactions: this.organisationViewModel.interactions,
        lookup: {
          categories,
          organisationTypes,
          sectors,
        },
        organisation,
        offers: offers.items,
        posts: emptyPageResponse(),
        projects: projects.items,
      }
    })
  }

  @Loading(FETCH_ORGANISATION_ACTIVITIES)
  @Action({
    commit: FETCH_ORGANISATION_ACTIVITIES,
    rawError: true,
  })
  public async fetchOrganisationActivities({
    organisationId,
    page,
    pageSize,
  }: OrganisationActivitiesPayload): Promise<OrganisationActivitiesPageResponse> {
    return Api.Organisations.getOrganisationActivities({
      organisationId,
      page,
      pageSize,
    })
  }

  @Loading(FETCH_ORGANISATION_INTERACTIONS)
  @Action({
    commit: FETCH_ORGANISATION_INTERACTIONS,
    rawError: true,
  })
  public async fetchOrganisationInteractions({
    organisationId,
    page,
    pageSize,
  }: OrganisationInteractionsPayload): Promise<OrganisationInteractionsPageResponse> {
    return Api.Organisations.getOrganisationInteractions({
      organisationId,
      page,
      pageSize,
    })
  }

  @Loading(FETCH_ORGANISATION_POSTS)
  @Action({
    commit: FETCH_ORGANISATION_POSTS,
    rawError: true,
  })
  public async fetchOrganisationPosts({
    organisationId,
    page,
    pageSize,
  }: FetchOrganisationPostsPayload): Promise<PostPageResponse> {
    return Api.Organisations.getOrganisationPosts({
      organisationId,
      page,
      pageSize,
    })
  }

  @Loading(FETCH_ORGANISATIONS)
  @Action({
    commit: FETCH_ORGANISATIONS,
    rawError: true,
  })
  public async fetchOrganisations(
    { page, pageSize }: FetchPagePayload = { page: 1, pageSize: 10 }
  ): Promise<OrganisationsViewModel> {
    const categories$ = Api.Lookup.getCategories().catch(() => [])
    const organisationsTypes$ = Api.Lookup.getOrganisationTypes().catch(() => [])

    const organisations$ = Api.Organisations.getOrganisations({
      page,
      pageSize,
    })

    return Promise.all([categories$, organisationsTypes$, organisations$]).then(
      ([categories, organisationTypes, organisations]) => {
        return {
          lookup: {
            categories,
            organisationTypes,
          },
          organisations,
        }
      }
    )
  }

  @Loading(FETCH_ORGANISATION_STATISTICS)
  @Action({
    commit: FETCH_ORGANISATION_STATISTICS,
    rawError: true,
  })
  public fetchOrganisationStatistics(payload: {
    organisationId: number
  }): Promise<StatisticsResponse> {
    return Api.Organisations.getStatisticsForOrganisation(payload)
  }

  @Loading(SEARCH_ORGANISATIONS)
  @Action({
    commit: SEARCH_ORGANISATIONS,
    rawError: true,
  })
  public searchOrganisations(payload: GetOrganisationsRequest): Promise<OrganisationPageResponse> {
    return Api.Organisations.getOrganisations(payload)
  }

  @Loading(UPDATE_ORGANISATION)
  @Action({
    commit: UPDATE_ORGANISATION,
    rawError: true,
  })
  public updateOrganisation(payload: UpdateOrganisationRequest) {
    return Api.Organisations.updateOrganisation(payload)
  }

  @Loading(UPDATE_ORGANISATION_POST)
  @Action({
    commit: UPDATE_ORGANISATION_POST,
    rawError: true,
  })
  public async updateOrganisationPost(payload: UpdateOrganisationPostPayload): Promise<Post> {
    return Api.Posts.updatePost({
      postId: payload.postId,
      postRequest: payload.post,
    })
  }
}
